import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { config } from '../config';

const backendApi = config.backendApi;

/**
 * PSA ticketing (docs/PsaIntegration.md): an organization's connections to its PSA,
 * which PSA company each customer is, and the tickets bound to an incident.
 *
 * Every endpoint answers 404 outside the `psa_ticketing` rollout, so callers gate on
 * `useRollout(ROLLOUTS.psaTicketing)` before enabling these queries.
 */

export type PsaConnectionStatus = 'active' | 'needs_attention' | 'disconnected';

export type PsaKind = 'autotask' | 'halo';

export interface PsaConnection {
  id: string;
  kind: PsaKind;
  /** The actor's label on the timeline: "Jane Doe via Autotask". */
  name: string;
  divisionId: string | null;
  endpoint: string;
  status: PsaConnectionStatus;
  lastError: string | null;
  settleMinutes: number;
  /**
   * The PSA's own settings, stored as its IDs: `AutotaskConfig` or `HaloConfig`
   * by `kind`. Every field is optional, so one type reads either.
   */
  config: AutotaskConfig & HaloConfig;
  connectedBy: string | null;
  createdAt: string;
  updatedAt: string;
  /** Whether the PSA calls Witina when a ticket changes; without it, within 5 minutes. */
  instantUpdates: InstantUpdates;
}

export interface InstantUpdates {
  /**
   * `setting_up` until Witina first tries to make the PSA's webhooks. Halo's are
   * added by hand in Halo, so a Halo connection is `waiting` until Halo first calls.
   */
  state: 'setting_up' | 'on' | 'off' | 'waiting';
  /** Why they're off: the PSA's refusal, or that it deactivated them. */
  reason: string | null;
}

/** Severity → the PSA's priority ID. `info` takes `low`'s when it has none. */
export type PriorityMap = Partial<Record<'critical' | 'high' | 'medium' | 'low' | 'info', number>>;

/** Mirrors the backend's `AutotaskConfig`; snake_case because it is stored as sent. */
export interface AutotaskConfig {
  queue_id?: number | null;
  ticket_category_id?: number | null;
  ticket_type?: number | null;
  source_id?: number | null;
  issue_type?: number | null;
  sub_issue_type?: number | null;
  priority_map?: PriorityMap;
  new_status_id?: number | null;
  ack_status_ids?: number[];
  recovery_status_id?: number | null;
  note_type_id?: number | null;
  note_publish?: number | null;
  due_in_hours?: number | null;
  link_udf?: string | null;
}

/** Mirrors the backend's `HaloConfig`; snake_case because it is stored as sent. */
export interface HaloConfig {
  tickettype_id?: number | null;
  /** Halo numbers priorities within each SLA; a severity with none takes Halo's default. */
  priority_map?: PriorityMap;
  new_status_id?: number | null;
  ack_status_ids?: number[];
  recovery_status_id?: number | null;
  /** The action outcome Witina's notes are written as; with none, no note is sent. */
  note_outcome_id?: number | null;
  /** Witina's notes are hidden from the end user unless the admin chose otherwise. */
  hidden_from_user?: boolean;
  team_id?: number | null;
  agent_id?: number | null;
}

/**
 * One customer's Autotask tickets, where they differ from the connection's
 * (decision 15): where a new ticket is filed, how it's classified, its
 * priorities, and the company's location and contract. Unset is the connection's.
 */
export interface AutotaskCustomer {
  queue_id?: number | null;
  ticket_category_id?: number | null;
  ticket_type?: number | null;
  source_id?: number | null;
  /** With its sub-issue type: a customer's issue type replaces the connection's pair. */
  issue_type?: number | null;
  sub_issue_type?: number | null;
  due_in_hours?: number | null;
  /** Only the severities the customer names. */
  priority_map?: PriorityMap;
  location_id?: number | null;
  contract_id?: number | null;
}

/** One customer's Halo tickets, where they differ from the connection's. */
export interface HaloCustomer {
  tickettype_id?: number | null;
  team_id?: number | null;
  agent_id?: number | null;
  priority_map?: PriorityMap;
  site_id?: number | null;
}

/** A customer's own ticket settings, `AutotaskCustomer` or `HaloCustomer` by the connection's kind. */
export type CustomerSettings = AutotaskCustomer & HaloCustomer;

export interface PicklistOption {
  value: string;
  label: string;
  active: boolean;
  /** The option this one belongs under: a sub-issue type's issue type. */
  parent?: string | null;
}

export interface Picklist {
  /** The setting it feeds: Autotask's field name (`queueID`, `publish`, …), Halo's setting (`status_id`, …). */
  field: string;
  options: PicklistOption[];
}

export interface PsaCompany {
  companyRef: string;
  name: string;
}

export interface PsaMapping {
  id: string;
  /** None for the default, which covers every customer without its own. */
  divisionId: string | null;
  companyRef: string;
  companyName: string;
  /** The customer's own ticket settings, only those set; null: the connection's. */
  extra: CustomerSettings | null;
}

export interface IncidentTicket {
  id: string;
  connectionId: string;
  connectionName: string;
  kind: string;
  ticketRef: string | null;
  ticketNumber: string | null;
  companyRef: string;
  /** `route`, `manual` or `linked`. */
  createdVia: string;
  psaStatus: string | null;
  closedInPsaAt: string | null;
  lastError: string | null;
  incidentCount: number;
  /** A route's ticket waiting out the settle delay: made then, if the incident is still open. */
  createAfter: string | null;
}

/** A connection that serves the incident and has no ticket for it yet. */
export interface AvailableConnection {
  id: string;
  name: string;
  kind: string;
  status: PsaConnectionStatus;
}

export interface IncidentTickets {
  tickets: IncidentTicket[];
  connections: AvailableConnection[];
}

const CONNECTIONS = ['psa-connections'] as const;

export function usePsaConnections(enabled = true) {
  return useQuery({
    queryKey: CONNECTIONS,
    enabled,
    queryFn: async () => {
      const res = await axios.get<{ connections: PsaConnection[] }>(`${backendApi}/api/psa/connections`, {
        withCredentials: true,
      });
      return res.data.connections;
    },
  });
}

interface ConnectCommon {
  name?: string;
  divisionId?: string | null;
}

export type ConnectInput =
  | (ConnectCommon & { kind: 'autotask'; username: string; secret: string; integrationCode: string })
  | (ConnectCommon & {
      kind: 'halo';
      /** The API details Halo's settings show. */
      authUrl: string;
      resourceUrl: string;
      /** A hosted Halo's tenant; none for one the MSP runs itself. */
      tenant?: string;
      clientId: string;
      clientSecret: string;
    });

export function useConnectPsa() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: ConnectInput) => {
      const res = await axios.post<PsaConnection>(`${backendApi}/api/psa/connections`, input, {
        withCredentials: true,
      });
      return res.data;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: CONNECTIONS }),
  });
}

export interface UpdateConnectionInput {
  name?: string;
  config?: AutotaskConfig | HaloConfig;
  settleMinutes?: number;
  username?: string;
  secret?: string;
  integrationCode?: string;
  authUrl?: string;
  resourceUrl?: string;
  /** Empty clears it. */
  tenant?: string;
  clientId?: string;
  clientSecret?: string;
}

/** How to add Witina's webhook in Halo: where it calls, and the Basic authentication it sends. */
export interface WebhookSetup {
  url: string;
  username: string;
  password: string;
}

const webhookKey = (connectionId: string) => ['psa-webhook', connectionId] as const;

/**
 * A Halo connection's webhook details. They include the password, and the backend
 * keeps each read in the activity log, so they're fetched only when asked for.
 */
export function useHaloWebhook(connectionId: string, enabled: boolean) {
  return useQuery({
    queryKey: webhookKey(connectionId),
    enabled,
    staleTime: Infinity,
    queryFn: async () => {
      const res = await axios.get<WebhookSetup>(`${backendApi}/api/psa/connections/${connectionId}/webhook`, {
        withCredentials: true,
      });
      return res.data;
    },
  });
}

/** A new password: the old one stops working at once. Answers with the new details. */
export function useRotateHaloWebhookPassword(connectionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await axios.post<WebhookSetup>(
        `${backendApi}/api/psa/connections/${connectionId}/webhook/password`,
        {},
        { withCredentials: true },
      );
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(webhookKey(connectionId), data);
      void queryClient.invalidateQueries({ queryKey: CONNECTIONS });
    },
  });
}

export function useUpdatePsaConnection(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateConnectionInput) => {
      const res = await axios.patch<PsaConnection>(`${backendApi}/api/psa/connections/${id}`, input, {
        withCredentials: true,
      });
      return res.data;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: CONNECTIONS }),
  });
}

export function useDeletePsaConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      axios.delete(`${backendApi}/api/psa/connections/${id}`, { withCredentials: true }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: CONNECTIONS }),
  });
}

/** The PSA's own lists, read live from the PSA: only fetched while the form is open. */
export function usePsaPicklists(connectionId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['psa-picklists', connectionId],
    enabled,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const res = await axios.get<{ picklists: Picklist[] }>(
        `${backendApi}/api/psa/connections/${connectionId}/picklists`,
        { withCredentials: true },
      );
      return res.data.picklists;
    },
  });
}

/** Companies whose name contains `query`; the backend wants at least two characters. */
export function usePsaCompanies(connectionId: string, query: string) {
  const q = query.trim();
  return useQuery({
    queryKey: ['psa-companies', connectionId, q],
    enabled: q.length >= 2,
    staleTime: 60_000,
    queryFn: async () => {
      const res = await axios.get<{ companies: PsaCompany[] }>(
        `${backendApi}/api/psa/connections/${connectionId}/companies`,
        { params: { q }, withCredentials: true },
      );
      return res.data.companies;
    },
  });
}

const mappingsKey = (connectionId: string) => ['psa-mappings', connectionId] as const;

export function usePsaMappings(connectionId: string) {
  return useQuery({
    queryKey: mappingsKey(connectionId),
    queryFn: async () => {
      const res = await axios.get<{ mappings: PsaMapping[] }>(
        `${backendApi}/api/psa/connections/${connectionId}/mappings`,
        { withCredentials: true },
      );
      return res.data.mappings;
    },
  });
}

export interface MappingInput {
  divisionId: string | null;
  companyRef: string;
  companyName: string;
  /** The customer's own ticket settings, whole; the backend keeps only those set. */
  extra?: CustomerSettings | null;
}

/** A company's own lists: an Autotask company's locations and contracts, a Halo client's sites. */
export function usePsaCompanyPicklists(connectionId: string, companyRef: string, enabled: boolean) {
  return useQuery({
    queryKey: ['psa-company-picklists', connectionId, companyRef],
    enabled,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const res = await axios.get<{ picklists: Picklist[] }>(
        `${backendApi}/api/psa/connections/${connectionId}/companies/${encodeURIComponent(companyRef)}/picklists`,
        { withCredentials: true },
      );
      return res.data.picklists;
    },
  });
}

export function usePutPsaMapping(connectionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: MappingInput) => {
      const res = await axios.put<PsaMapping>(`${backendApi}/api/psa/connections/${connectionId}/mappings`, input, {
        withCredentials: true,
      });
      return res.data;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: mappingsKey(connectionId) }),
  });
}

export function useDeletePsaMapping(connectionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (mappingId: string) =>
      axios.delete(`${backendApi}/api/psa/connections/${connectionId}/mappings/${mappingId}`, {
        withCredentials: true,
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: mappingsKey(connectionId) }),
  });
}

const ticketsKey = (incidentId: string) => ['incident-tickets', incidentId] as const;

export function useIncidentTickets(incidentId: string, enabled: boolean) {
  return useQuery({
    queryKey: ticketsKey(incidentId),
    enabled,
    queryFn: async () => {
      const res = await axios.get<IncidentTickets>(`${backendApi}/api/incidents/${incidentId}/tickets`, {
        withCredentials: true,
      });
      return res.data;
    },
  });
}

/** Both answer with the incident's tickets as they now stand. */
export function useCreateIncidentTicket(incidentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (connectionId: string) => {
      const res = await axios.post<IncidentTickets>(
        `${backendApi}/api/incidents/${incidentId}/tickets`,
        { connectionId },
        { withCredentials: true },
      );
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(ticketsKey(incidentId), data);
      void queryClient.invalidateQueries({ queryKey: ['incident-events', incidentId] });
    },
  });
}

export interface LinkTicketInput {
  connectionId: string;
  ticketNumber: string;
  /** Link although the ticket's company isn't the customer's mapping. */
  confirmCompany?: boolean;
}

export function useLinkIncidentTicket(incidentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: LinkTicketInput) => {
      const res = await axios.post<IncidentTickets>(`${backendApi}/api/incidents/${incidentId}/tickets/link`, input, {
        withCredentials: true,
      });
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(ticketsKey(incidentId), data);
      void queryClient.invalidateQueries({ queryKey: ['incident-events', incidentId] });
    },
  });
}
